import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { api, resolveMedia } from "@/lib/api";
import { usePublicData } from "@/lib/publicData";
import { PageHero, Container, Reveal, PHOTOS, SERVICE_ICONS } from "@/components/public/parts";
import { CheckCircle2, ArrowRight, Heart, Loader2 } from "lucide-react";

const BENEFITS = ["Person-centered planning", "Trained, background-checked staff", "Flexible scheduling", "Community integration", "Family collaboration", "Dignity and respect"];

export default function ServiceDetail() {
  const { slug } = useParams();
  const data = usePublicData();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Container className="py-24 text-center"><h1 className="font-heading text-3xl font-bold">Service not found</h1><Link to="/services" className="text-primary mt-4 inline-block">← Back to services</Link></Container>;
  if (!svc) return <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const Icon = SERVICE_ICONS[svc.icon] || Heart;
  const img = resolveMedia((data?.sections || {}).hero?.image_url || PHOTOS.care);

  return (
    <>
      <Seo title={`${svc.name} | Love For Mankind`} description={svc.description?.slice(0, 155)} path={`/services/${slug}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", name: svc.name, description: svc.description, provider: { "@type": "Organization", name: "Love For Mankind" } }} />
      <PageHero eyebrow="Service" title={svc.name} sub={svc.description} breadcrumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: svc.name }]} />
      <Container className="py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center"><Icon className="w-7 h-7 text-primary" /></div>
            <h2 className="font-heading text-3xl font-bold mt-5">About this service</h2>
            <p className="text-[17px] text-muted-foreground mt-4 leading-[1.75]">{svc.description}</p>
            <p className="text-[17px] text-muted-foreground mt-4 leading-[1.75]">Our team works closely with each individual and their family to build a plan that fits their goals, preferences and daily life — always with dignity, respect and genuine human connection.</p>
            <h3 className="font-heading text-2xl font-semibold mt-10">Benefits</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {BENEFITS.map((b) => <div key={b} className="flex items-center gap-2.5 text-[16px]"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> {b}</div>)}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-1">
          <div className="sticky top-32 rounded-2xl overflow-hidden border border-border">
            <img src={img} alt={svc.name} className="w-full aspect-[4/3] object-cover" />
            <div className="p-6 bg-card">
              <p className="font-heading font-semibold text-lg">Interested in {svc.name}?</p>
              <p className="text-sm text-muted-foreground mt-1">Start a request and we'll be in touch.</p>
              <Link to={`/request-services?service=${encodeURIComponent(svc.name)}`}>
                <button className="rounded-full bg-primary text-primary-foreground font-semibold w-full h-12 mt-4 hover:bg-primary/90 transition-colors flex items-center justify-center gap-2" data-testid="request-this-service">Request This Service <ArrowRight className="w-4 h-4" /></button>
              </Link>
              <Link to="/referral"><button className="rounded-full border border-border font-semibold w-full h-11 mt-2.5 hover:border-primary hover:text-primary transition-colors">Make a Referral</button></Link>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}
